import React, { useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import CustomChartTooltip from "../CustomChartTooltip";
import { getNativeOptionsChart } from "../getNativeOptionsChart";
import { theme, ANChartJSWrapper } from "@answersnow/pkg-theme";

const { yellow, pink, red, orange } = theme.palette;

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const optionsBarChart = getNativeOptionsChart({
  categoryPercentage: {
    sm: 0.9,
    lg: 0.65,
  },
  ticksFont: {
    sm: 10,
    lg: 14,
  },
  barPercentage: {
    sm: 0.8,
    lg: 0.9,
  },
  barBorderRadius: {
    sm: 6,
    lg: 6,
  },
  barBorderWidth: {
    sm: 1,
    lg: 2,
  },
  barMaxBarThickness: {
    sm: 14,
    lg: 16,
  },
});

/**
 * @component
 * @description `CancellationsChart` component to display the cancellations and no-shows chart.
 * @param {Object} props
 * @param {string} props.period - The time unit filter (weeks or months)
 * @param {Object} props.hoursData - The data for the chart
 * @param {boolean} props.isSmallScreen - Whether it's a small screen (optional)
 * @returns {JSX.Element} The CancellationsChart component
 */
const CancellationsChart = ({ hoursData, period, labels }) => {
  const [chartContext, setChartContext] = useState(null);
  const chartRef = useRef(null);

  const { scheduledHours, yourCancellations, noShows, clientCancellations, other, dates } =
    hoursData;

  const categoryPercentage = period === "weeks" ? 0.55 : 0.8;

  const data = {
    labels,
    datasets: [
      {
        label: "Scheduled hours",
        data: scheduledHours,
        backgroundColor: yellow[600],
        stack: "stack1",
        dates,
        period,
        skipNull: true,
        categoryPercentage,
      },
      {
        label: "Your cancellations",
        data: yourCancellations,
        backgroundColor: orange[600],
        stack: "stack2",
        dates,
        period,
        skipNull: true,
        minBarLength: 8,
        categoryPercentage,
      },
      {
        label: "Client cancellations",
        data: clientCancellations,
        backgroundColor: red[600],
        stack: "stack3",
        dates,
        period,
        skipNull: true,
        categoryPercentage,
        minBarLength: 8,
      },

      {
        label: "Client no-shows",
        data: noShows,
        backgroundColor: red[400],
        stack: "stack3",
        dates,
        period,
        skipNull: true,
        categoryPercentage,
      },
      {
        label: "Other",
        data: other,
        backgroundColor: pink[500],
        stack: "stack4",
        dates,
        period,
        skipNull: true,
        categoryPercentage,
      },
    ],
  };

  const handleChartMouseEnter = () => {
    setChartContext({
      config: chartRef.current?.config,
      tooltip: chartRef.current?.tooltip,
    });
  };

  const handleChartMouseLeave = () => {
    setChartContext(null);
  };

  return (
    <ANChartJSWrapper
      chartContext={chartContext}
      customTooltipContent={
        <CustomChartTooltip config={chartContext?.config} tooltip={chartContext?.tooltip} />
      }
      customTooltipProps={{ width: "187px" }}
    >
      <Bar
        options={optionsBarChart}
        data={data}
        ref={chartRef}
        onMouseMove={handleChartMouseEnter}
        onMouseLeave={handleChartMouseLeave}
      />
    </ANChartJSWrapper>
  );
};

export default CancellationsChart;
